import {
  getFaqs, createFaq, updateFaq, deleteFaq,
  getNews, createNews, updateNews, deleteNews,
  getLegal, createLegal, updateLegal, deleteLegal,
  getSocialLinks, createSocialLink, updateSocialLink, deleteSocialLink,
} from '../admin-api';
import { esc, showConfirm, formField } from '../admin-ui';
import type { FaqItem, NewsItem, LegalPage, SocialLink } from '../admin-api';
import { ApiError } from '../../../core/network/api';

/**
 * Every save here used to end in an empty `catch`, so a rejected
 * request looked exactly like a button that did nothing — which is how the
 * Social form shipped answering every save with a 400 nobody could see.
 * Failures are now written into the form they came from.
 */
export function cmsErrorText(err: unknown): string {
  if (err instanceof ApiError) {
    const msg = (err.details as { message?: unknown } | undefined)?.message ?? err.message;
    const text = Array.isArray(msg) ? msg.join('; ') : String(msg);
    return err.status ? `${text} (${err.status})` : text;
  }
  return err instanceof Error ? err.message : 'Something went wrong';
}

/** Core only accepts lowercase-dash slugs (`SLUG_PATTERN` in core's cms.dto). */
export function slugify(text: string): string {
  return text
    .normalize('NFKD').replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 100);
}

export function isHttpUrl(value: string): boolean {
  try {
    const u = new URL(value);
    return u.protocol === 'https:' || u.protocol === 'http:';
  } catch {
    return false;
  }
}

function errorSlot(id: string): string {
  return `<p id="${id}" role="alert" class="hidden text-sm text-red-400 mt-3"></p>`;
}

function setError(el: HTMLElement, id: string, message: string | null): void {
  const slot = el.querySelector<HTMLElement>(`#${id}`);
  if (!slot) return;
  slot.textContent = message ?? '';
  slot.classList.toggle('hidden', !message);
}

/** Runs one save: disables the button, reports a failure in the form's error slot. */
async function submit(el: HTMLElement, errorId: string, button: Element | null, work: () => Promise<void>): Promise<void> {
  const btn = button as HTMLButtonElement | null;
  if (btn?.disabled) return;
  setError(el, errorId, null);
  if (btn) btn.disabled = true;
  try {
    await work();
  } catch (err) {
    setError(el, errorId, cmsErrorText(err));
  } finally {
    if (btn && btn.isConnected) btn.disabled = false;
  }
}

type CmsSubSection = 'faq' | 'news' | 'legal' | 'social';

export async function mountCms(container: HTMLElement, subSection: string): Promise<() => void> {
  let alive = true;
  const section = subSection as CmsSubSection;

  switch (section) {
    case 'faq': await mountFaq(container); break;
    case 'news': await mountNews(container); break;
    case 'legal': await mountLegalPages(container); break;
    case 'social': await mountSocial(container); break;
    default: container.innerHTML = `<p class="text-muted p-4">Unknown CMS section.</p>`;
  }

  return () => { alive = false; };

  async function mountFaq(el: HTMLElement): Promise<void> {
    if (!alive) return;
    let items: FaqItem[] = [];
    let loadError: string | null = null;
    try { items = await getFaqs(); } catch (err) { loadError = cmsErrorText(err); }

    function render(): void {
      el.innerHTML = `
        <div class="flex items-center justify-between mb-4">
          <h2 class="font-heading text-gold text-lg">FAQ</h2>
          <button id="adm-faq-add" class="btn btn-gold min-h-11 px-4 text-sm">Add FAQ</button>
        </div>
        <div id="adm-faq-form" class="hidden frame-pewter p-4 mb-4">
          <input type="hidden" id="adm-faq-edit-id" value="" />
          ${formField('Question', 'adm-faq-q', 'text')}
          <label class="block text-sm text-muted mb-1 mt-3">Answer</label>
          <textarea id="adm-faq-a" rows="4"
            class="w-full min-h-11 px-3 py-2 bg-surface-2 text-text border border-border rounded outline-none focus:border-gold text-sm"></textarea>
          <div class="flex gap-2 mt-3">
            <button id="adm-faq-save" class="btn btn-gold min-h-11 px-4 text-sm">Save</button>
            <button id="adm-faq-cancel" class="btn btn-stone min-h-11 px-4 text-sm">Cancel</button>
          </div>
          ${errorSlot('adm-faq-error')}
        </div>
        ${errorSlot('adm-faq-list-error')}
        <div class="space-y-3">
          ${items.length === 0 ? `<p class="text-muted text-sm">No FAQ entries.</p>` : items.map((item) => `
            <div class="frame-pewter p-3">
              <p class="font-medium text-text mb-1">${esc(item.question)}</p>
              <p class="text-sm text-muted mb-2">${esc(item.answer)}</p>
              <div class="flex gap-2">
                <button class="adm-faq-edit btn btn-stone min-h-11 px-3 text-xs" data-id="${esc(item.id)}">Edit</button>
                <button class="adm-faq-del btn btn-stone min-h-11 px-3 text-xs text-red-400" data-id="${esc(item.id)}">Delete</button>
              </div>
            </div>
          `).join('')}
        </div>
      `;
      wireFaq();
      setError(el, 'adm-faq-list-error', loadError);
    }

    function wireFaq(): void {
      const form = el.querySelector<HTMLElement>('#adm-faq-form')!;
      const editId = el.querySelector<HTMLInputElement>('#adm-faq-edit-id')!;
      const qInput = el.querySelector<HTMLInputElement>('#adm-faq-q')!;
      const aInput = el.querySelector<HTMLTextAreaElement>('#adm-faq-a')!;

      el.querySelector('#adm-faq-add')?.addEventListener('click', () => {
        editId.value = '';
        qInput.value = '';
        aInput.value = '';
        setError(el, 'adm-faq-error', null);
        form.classList.remove('hidden');
      });
      el.querySelector('#adm-faq-cancel')?.addEventListener('click', () => form.classList.add('hidden'));

      el.querySelector('#adm-faq-save')?.addEventListener('click', (e) => {
        const question = qInput.value.trim();
        const answer = aInput.value.trim();
        if (!question || !answer) { setError(el, 'adm-faq-error', 'Question and answer are both required.'); return; }
        void submit(el, 'adm-faq-error', e.currentTarget as Element, async () => {
          if (editId.value) {
            await updateFaq(editId.value, { question, answer });
          } else {
            await createFaq({ question, answer });
          }
          items = await getFaqs();
          loadError = null;
          render();
        });
      });

      el.querySelectorAll<HTMLButtonElement>('.adm-faq-edit').forEach((btn) => {
        btn.addEventListener('click', () => {
          const item = items.find((i) => i.id === btn.dataset.id);
          if (!item) return;
          editId.value = item.id;
          qInput.value = item.question;
          aInput.value = item.answer;
          form.classList.remove('hidden');
        });
      });

      el.querySelectorAll<HTMLButtonElement>('.adm-faq-del').forEach((btn) => {
        btn.addEventListener('click', () => {
          showConfirm('Delete this FAQ entry?', () => submit(el, 'adm-faq-list-error', null, async () => {
            await deleteFaq(btn.dataset.id!);
            items = await getFaqs();
            render();
          }));
        });
      });
    }

    render();
  }

  async function mountNews(el: HTMLElement): Promise<void> {
    if (!alive) return;
    let articles: NewsItem[] = [];
    let loadError: string | null = null;
    try { const res = await getNews({ per_page: 100 }); articles = res.items; } catch (err) { loadError = cmsErrorText(err); }

    function render(): void {
      el.innerHTML = `
        <div class="flex items-center justify-between mb-4">
          <h2 class="font-heading text-gold text-lg">News</h2>
          <button id="adm-news-add" class="btn btn-gold min-h-11 px-4 text-sm">Add Article</button>
        </div>
        <div id="adm-news-form" class="hidden frame-pewter p-4 mb-4">
          <input type="hidden" id="adm-news-edit-id" value="" />
          ${formField('Title', 'adm-news-title', 'text')}
          ${formField('Slug', 'adm-news-slug', 'text')}
          <label class="block text-sm text-muted mb-1 mt-3">Content</label>
          <textarea id="adm-news-content" rows="6"
            class="w-full min-h-11 px-3 py-2 bg-surface-2 text-text border border-border rounded outline-none focus:border-gold text-sm"></textarea>
          <label class="flex items-center gap-2 mt-3 text-sm text-text cursor-pointer">
            <input type="checkbox" id="adm-news-published" class="min-w-[18px] min-h-[18px]" />
            Published
          </label>
          <div class="flex gap-2 mt-3">
            <button id="adm-news-save" class="btn btn-gold min-h-11 px-4 text-sm">Save</button>
            <button id="adm-news-cancel" class="btn btn-stone min-h-11 px-4 text-sm">Cancel</button>
          </div>
          ${errorSlot('adm-news-error')}
        </div>
        ${errorSlot('adm-news-list-error')}
        <div class="space-y-3">
          ${articles.length === 0 ? `<p class="text-muted text-sm">No articles.</p>` : articles.map((a) => `
            <div class="frame-pewter p-3">
              <div class="flex items-start justify-between gap-2">
                <div>
                  <p class="font-medium text-text">${esc(a.title)}</p>
                  <p class="text-xs text-muted mt-0.5">${esc(a.slug)} · <span class="${a.published ? 'text-green-400' : 'text-muted'}">${a.published ? 'Published' : 'Draft'}</span></p>
                </div>
                <span class="text-xs text-muted whitespace-nowrap">${a.createdAt ? new Date(a.createdAt).toLocaleDateString() : ''}</span>
              </div>
              <div class="flex gap-2 mt-2">
                <button class="adm-news-edit btn btn-stone min-h-11 px-3 text-xs" data-id="${esc(a.id)}">Edit</button>
                <button class="adm-news-del btn btn-stone min-h-11 px-3 text-xs text-red-400" data-id="${esc(a.id)}">Delete</button>
              </div>
            </div>
          `).join('')}
        </div>
      `;
      wireNews();
      setError(el, 'adm-news-list-error', loadError);
    }

    function wireNews(): void {
      const form = el.querySelector<HTMLElement>('#adm-news-form')!;
      const editId = el.querySelector<HTMLInputElement>('#adm-news-edit-id')!;

      el.querySelector('#adm-news-add')?.addEventListener('click', () => {
        editId.value = '';
        (el.querySelector<HTMLInputElement>('#adm-news-title'))!.value = '';
        (el.querySelector<HTMLInputElement>('#adm-news-slug'))!.value = '';
        (el.querySelector<HTMLTextAreaElement>('#adm-news-content'))!.value = '';
        (el.querySelector<HTMLInputElement>('#adm-news-published'))!.checked = false;
        setError(el, 'adm-news-error', null);
        form.classList.remove('hidden');
      });
      el.querySelector('#adm-news-cancel')?.addEventListener('click', () => form.classList.add('hidden'));

      el.querySelector('#adm-news-save')?.addEventListener('click', (e) => {
        const title = (el.querySelector<HTMLInputElement>('#adm-news-title'))!.value.trim();
        const slugInput = (el.querySelector<HTMLInputElement>('#adm-news-slug'))!;
        const content = (el.querySelector<HTMLTextAreaElement>('#adm-news-content'))!.value.trim();
        const published = (el.querySelector<HTMLInputElement>('#adm-news-published'))!.checked;
        if (!title || !content) { setError(el, 'adm-news-error', 'Title and content are both required.'); return; }
        const slug = slugify(slugInput.value.trim() || title);
        if (!slug) { setError(el, 'adm-news-error', 'Slug needs at least one letter or digit.'); return; }
        slugInput.value = slug;
        void submit(el, 'adm-news-error', e.currentTarget as Element, async () => {
          // The public feed (and the landing) orders and dates articles by
          // publishedAt. Core stamps "now" whenever a body says published
          // without a date, so a typo fix would bump an old article to the
          // top — send the original date back on edits, "now" only the first time.
          const prev = editId.value ? articles.find((i) => i.id === editId.value) : undefined;
          const publishedAt = published ? (prev?.publishedAt ?? new Date().toISOString()) : undefined;
          if (editId.value) {
            await updateNews(editId.value, { title, slug, content, published, ...(publishedAt ? { publishedAt } : {}) });
          } else {
            await createNews({ title, slug, content, published, ...(publishedAt ? { publishedAt } : {}) });
          }
          const res = await getNews({ per_page: 100 });
          articles = res.items;
          loadError = null;
          render();
        });
      });

      el.querySelectorAll<HTMLButtonElement>('.adm-news-edit').forEach((btn) => {
        btn.addEventListener('click', () => {
          const a = articles.find((i) => i.id === btn.dataset.id);
          if (!a) return;
          editId.value = a.id;
          (el.querySelector<HTMLInputElement>('#adm-news-title'))!.value = a.title;
          (el.querySelector<HTMLInputElement>('#adm-news-slug'))!.value = a.slug;
          (el.querySelector<HTMLTextAreaElement>('#adm-news-content'))!.value = a.content;
          (el.querySelector<HTMLInputElement>('#adm-news-published'))!.checked = a.published;
          form.classList.remove('hidden');
        });
      });

      el.querySelectorAll<HTMLButtonElement>('.adm-news-del').forEach((btn) => {
        btn.addEventListener('click', () => {
          showConfirm('Delete this article?', () => submit(el, 'adm-news-list-error', null, async () => {
            await deleteNews(btn.dataset.id!);
            const res = await getNews({ per_page: 100 });
            articles = res.items;
            render();
          }));
        });
      });
    }

    render();
  }

  async function mountLegalPages(el: HTMLElement): Promise<void> {
    if (!alive) return;
    let pages: LegalPage[] = [];
    let loadError: string | null = null;
    try { pages = await getLegal(); } catch (err) { loadError = cmsErrorText(err); }

    function render(): void {
      el.innerHTML = `
        <div class="flex items-center justify-between mb-4">
          <h2 class="font-heading text-gold text-lg">Legal Pages</h2>
          <button id="adm-legal-add" class="btn btn-gold min-h-11 px-4 text-sm">Add Page</button>
        </div>
        <div id="adm-legal-form" class="hidden frame-pewter p-4 mb-4">
          <input type="hidden" id="adm-legal-edit-id" value="" />
          ${formField('Title', 'adm-legal-title', 'text')}
          ${formField('Slug', 'adm-legal-slug', 'text')}
          <label class="block text-sm text-muted mb-1 mt-3">Content</label>
          <textarea id="adm-legal-content" rows="8"
            class="w-full min-h-11 px-3 py-2 bg-surface-2 text-text border border-border rounded outline-none focus:border-gold text-sm"></textarea>
          <div class="flex gap-2 mt-3">
            <button id="adm-legal-save" class="btn btn-gold min-h-11 px-4 text-sm">Save</button>
            <button id="adm-legal-cancel" class="btn btn-stone min-h-11 px-4 text-sm">Cancel</button>
          </div>
          ${errorSlot('adm-legal-error')}
        </div>
        ${errorSlot('adm-legal-list-error')}
        <div class="space-y-3">
          ${pages.length === 0 ? `<p class="text-muted text-sm">No legal pages.</p>` : pages.map((p) => `
            <div class="frame-pewter p-3 flex items-center justify-between">
              <div>
                <p class="font-medium text-text">${esc(p.title)}</p>
                <p class="text-xs text-muted">${esc(p.slug)}</p>
              </div>
              <div class="flex gap-2">
                <button class="adm-legal-edit btn btn-stone min-h-11 px-3 text-xs" data-id="${esc(p.id)}">Edit</button>
                <button class="adm-legal-del btn btn-stone min-h-11 px-3 text-xs text-red-400" data-id="${esc(p.id)}">Delete</button>
              </div>
            </div>
          `).join('')}
        </div>
      `;
      wireLegal();
      setError(el, 'adm-legal-list-error', loadError);
    }

    function wireLegal(): void {
      const form = el.querySelector<HTMLElement>('#adm-legal-form')!;
      const editId = el.querySelector<HTMLInputElement>('#adm-legal-edit-id')!;

      el.querySelector('#adm-legal-add')?.addEventListener('click', () => {
        editId.value = '';
        (el.querySelector<HTMLInputElement>('#adm-legal-title'))!.value = '';
        (el.querySelector<HTMLInputElement>('#adm-legal-slug'))!.value = '';
        (el.querySelector<HTMLTextAreaElement>('#adm-legal-content'))!.value = '';
        setError(el, 'adm-legal-error', null);
        form.classList.remove('hidden');
      });
      el.querySelector('#adm-legal-cancel')?.addEventListener('click', () => form.classList.add('hidden'));

      el.querySelector('#adm-legal-save')?.addEventListener('click', (e) => {
        const title = (el.querySelector<HTMLInputElement>('#adm-legal-title'))!.value.trim();
        const slugInput = (el.querySelector<HTMLInputElement>('#adm-legal-slug'))!;
        const content = (el.querySelector<HTMLTextAreaElement>('#adm-legal-content'))!.value.trim();
        if (!title || !content) { setError(el, 'adm-legal-error', 'Title and content are both required.'); return; }
        const slug = slugify(slugInput.value.trim() || title);
        if (!slug) { setError(el, 'adm-legal-error', 'Slug needs at least one letter or digit.'); return; }
        slugInput.value = slug;
        void submit(el, 'adm-legal-error', e.currentTarget as Element, async () => {
          if (editId.value) {
            await updateLegal(editId.value, { title, slug, content });
          } else {
            await createLegal({ title, slug, content });
          }
          pages = await getLegal();
          loadError = null;
          render();
        });
      });

      el.querySelectorAll<HTMLButtonElement>('.adm-legal-edit').forEach((btn) => {
        btn.addEventListener('click', () => {
          const p = pages.find((i) => i.id === btn.dataset.id);
          if (!p) return;
          editId.value = p.id;
          (el.querySelector<HTMLInputElement>('#adm-legal-title'))!.value = p.title;
          (el.querySelector<HTMLInputElement>('#adm-legal-slug'))!.value = p.slug;
          (el.querySelector<HTMLTextAreaElement>('#adm-legal-content'))!.value = p.content;
          form.classList.remove('hidden');
        });
      });

      el.querySelectorAll<HTMLButtonElement>('.adm-legal-del').forEach((btn) => {
        btn.addEventListener('click', () => {
          showConfirm('Delete this legal page?', () => submit(el, 'adm-legal-list-error', null, async () => {
            await deleteLegal(btn.dataset.id!);
            pages = await getLegal();
            render();
          }));
        });
      });
    }

    render();
  }

  async function mountSocial(el: HTMLElement): Promise<void> {
    if (!alive) return;
    let links: SocialLink[] = [];
    let loadError: string | null = null;
    try { links = await getSocialLinks(); } catch (err) { loadError = cmsErrorText(err); }

    const PLATFORMS = ['twitter', 'discord', 'telegram', 'instagram', 'youtube', 'tiktok', 'facebook', 'reddit', 'other'];

    function render(): void {
      el.innerHTML = `
        <div class="flex items-center justify-between mb-4">
          <h2 class="font-heading text-gold text-lg">Social Links</h2>
          <button id="adm-social-add" class="btn btn-gold min-h-11 px-4 text-sm">Add Link</button>
        </div>
        <div id="adm-social-form" class="hidden frame-pewter p-4 mb-4">
          <input type="hidden" id="adm-social-edit-id" value="" />
          <label class="block text-sm text-muted mb-1">Platform</label>
          <select id="adm-social-platform"
            class="w-full min-h-11 px-3 bg-surface-2 text-text border border-border rounded outline-none focus:border-gold text-sm mb-3">
            ${PLATFORMS.map((p) => `<option value="${p}">${p[0].toUpperCase() + p.slice(1)}</option>`).join('')}
          </select>
          ${formField('URL', 'adm-social-url', 'url', '', { placeholder: 'https://' })}
          ${formField('Order (lower shows first)', 'adm-social-order', 'number', '0')}
          <label class="flex items-center gap-2 text-sm text-text cursor-pointer min-h-11">
            <input type="checkbox" id="adm-social-active" class="min-w-[18px] min-h-[18px]" checked />
            Show in the footer
          </label>
          <div class="flex gap-2 mt-3">
            <button id="adm-social-save" class="btn btn-gold min-h-11 px-4 text-sm">Save</button>
            <button id="adm-social-cancel" class="btn btn-stone min-h-11 px-4 text-sm">Cancel</button>
          </div>
          ${errorSlot('adm-social-error')}
        </div>
        ${errorSlot('adm-social-list-error')}
        <div class="space-y-3">
          ${links.length === 0 ? `<p class="text-muted text-sm">No social links.</p>` : links.map((l) => `
            <div class="frame-pewter p-3 flex items-center justify-between gap-2">
              <div class="min-w-0">
                <p class="font-medium text-text">${esc(l.platform)}${l.active === false ? ` <span class="text-xs text-muted">(hidden)</span>` : ''}</p>
                <p class="text-xs text-muted truncate">${esc(l.url)}</p>
              </div>
              <div class="flex gap-2 shrink-0">
                <button class="adm-social-edit btn btn-stone min-h-11 px-3 text-xs" data-id="${esc(l.id)}">Edit</button>
                <button class="adm-social-del btn btn-stone min-h-11 px-3 text-xs text-red-400" data-id="${esc(l.id)}">Delete</button>
              </div>
            </div>
          `).join('')}
        </div>
      `;
      wireSocial();
      setError(el, 'adm-social-list-error', loadError);
    }

    function wireSocial(): void {
      const form = el.querySelector<HTMLElement>('#adm-social-form')!;
      const editId = el.querySelector<HTMLInputElement>('#adm-social-edit-id')!;

      el.querySelector('#adm-social-add')?.addEventListener('click', () => {
        editId.value = '';
        (el.querySelector<HTMLSelectElement>('#adm-social-platform'))!.value = 'twitter';
        (el.querySelector<HTMLInputElement>('#adm-social-url'))!.value = '';
        (el.querySelector<HTMLInputElement>('#adm-social-order'))!.value = String(links.length);
        (el.querySelector<HTMLInputElement>('#adm-social-active'))!.checked = true;
        setError(el, 'adm-social-error', null);
        form.classList.remove('hidden');
      });
      el.querySelector('#adm-social-cancel')?.addEventListener('click', () => form.classList.add('hidden'));

      el.querySelector('#adm-social-save')?.addEventListener('click', (e) => {
        const platform = (el.querySelector<HTMLSelectElement>('#adm-social-platform'))!.value;
        const url = (el.querySelector<HTMLInputElement>('#adm-social-url'))!.value.trim();
        const sortOrder = Math.max(0, Math.trunc(Number((el.querySelector<HTMLInputElement>('#adm-social-order'))!.value) || 0));
        const active = (el.querySelector<HTMLInputElement>('#adm-social-active'))!.checked;
        if (!isHttpUrl(url)) { setError(el, 'adm-social-error', 'Enter a full link starting with https://'); return; }
        void submit(el, 'adm-social-error', e.currentTarget as Element, async () => {
          const body = { platform, url, sortOrder, active };
          if (editId.value) {
            await updateSocialLink(editId.value, body);
          } else {
            await createSocialLink(body);
          }
          links = await getSocialLinks();
          loadError = null;
          render();
        });
      });

      el.querySelectorAll<HTMLButtonElement>('.adm-social-edit').forEach((btn) => {
        btn.addEventListener('click', () => {
          const l = links.find((i) => i.id === btn.dataset.id);
          if (!l) return;
          editId.value = l.id;
          (el.querySelector<HTMLSelectElement>('#adm-social-platform'))!.value = l.platform;
          (el.querySelector<HTMLInputElement>('#adm-social-url'))!.value = l.url;
          (el.querySelector<HTMLInputElement>('#adm-social-order'))!.value = String(l.sortOrder ?? 0);
          (el.querySelector<HTMLInputElement>('#adm-social-active'))!.checked = l.active !== false;
          setError(el, 'adm-social-error', null);
          form.classList.remove('hidden');
        });
      });

      el.querySelectorAll<HTMLButtonElement>('.adm-social-del').forEach((btn) => {
        btn.addEventListener('click', () => {
          showConfirm('Delete this social link?', () => submit(el, 'adm-social-list-error', null, async () => {
            await deleteSocialLink(btn.dataset.id!);
            links = await getSocialLinks();
            render();
          }));
        });
      });
    }

    render();
  }
}
